// src/hooks/useFileUpload.js
import { useState, useRef } from 'react';
import { isVisionModel } from '../utils/modelUtils';
import * as pdfjsLib from 'pdfjs-dist';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

export const useFileUpload = (selectedModel, modelsData = null) => {
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  const getModelCapabilities = (modelId) => {
    if (modelsData && modelsData.models) {
      const model = modelsData.models.find(m => m.id === modelId);
      return model?.capabilities || { vision: false };
    }
    // Fallback to checking known vision models
    const visionModels = ['dali', 'nader', 'samata', 'claude-dev'];
    return { vision: visionModels.includes(modelId) };
  };

  const getAcceptedFiles = (modelId) => {
    if (modelsData && modelsData.models) {
      const model = modelsData.models.find(m => m.id === modelId);
      return model?.acceptedFiles || ['txt'];
    }
    return ['txt'];
  };

  // Function to extract text from PDF
  const extractPDFText = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += `\n--- Page ${i} ---\n${pageText}\n`;
      }
      
      return fullText.trim();
    } catch (error) {
      console.error('Error extracting PDF text:', error);
      throw new Error(`Failed to extract text from PDF: ${error.message}`);
    }
  };

  // Smart image compression that handles all formats intelligently
  const compressImage = (file, quality = 0.7, maxWidth = 1920, maxHeight = 1080) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        try {
          // Calculate new dimensions while maintaining aspect ratio
          let { width, height } = img;
          const originalWidth = width;
          const originalHeight = height;
          
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.floor(width * ratio);
            height = Math.floor(height * ratio);
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw image
          ctx.drawImage(img, 0, 0, width, height);
          
          // Always try multiple compression strategies and pick the best one
          const compressionAttempts = [];
          
          // Strategy 1: Convert to JPEG (best for photos)
          compressionAttempts.push({
            format: 'image/jpeg',
            quality: quality,
            name: 'JPEG conversion'
          });
          
          // Strategy 2: More aggressive JPEG
          compressionAttempts.push({
            format: 'image/jpeg',
            quality: Math.max(0.4, quality - 0.3),
            name: 'Aggressive JPEG'
          });
          
          // Strategy 3: Keep original format but compress (for WEBP/already compressed)
          if (file.type === 'image/webp') {
            compressionAttempts.push({
              format: 'image/webp',
              quality: Math.max(0.3, quality - 0.4),
              name: 'Recompressed WEBP'
            });
          }
          
          // Strategy 4: PNG only if original had transparency
          const imageData = ctx.getImageData(0, 0, width, height);
          let hasTransparency = false;
          for (let i = 3; i < Math.min(1000, imageData.data.length); i += 4) {
            if (imageData.data[i] < 255) {
              hasTransparency = true;
              break;
            }
          }
          
          if (hasTransparency) {
            compressionAttempts.push({
              format: 'image/png',
              quality: 0.8,
              name: 'PNG with transparency'
            });
          }
          
          // Try each compression strategy
          let bestResult = null;
          let attemptIndex = 0;
          
          const tryNextCompression = () => {
            if (attemptIndex >= compressionAttempts.length) {
              // All attempts done, return the best result
              if (bestResult) {
                resolve(bestResult);
              } else {
                reject(new Error('All compression attempts failed'));
              }
              return;
            }
            
            const attempt = compressionAttempts[attemptIndex];
            attemptIndex++;
            
            canvas.toBlob((blob) => {
              if (blob) {
                const compressionRatio = ((file.size - blob.size) / file.size * 100);
                
                // This compression is good if it's better than our current best
                if (!bestResult || blob.size < bestResult.size) {
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    bestResult = {
                      data: e.target.result,
                      size: blob.size,
                      compressed: true,
                      originalFormat: file.type,
                      outputFormat: attempt.format,
                      strategy: attempt.name,
                      compressionRatio: compressionRatio,
                      dimensionsChanged: width !== originalWidth || height !== originalHeight
                    };
                    tryNextCompression();
                  };
                  reader.readAsDataURL(blob);
                } else {
                  tryNextCompression();
                }
              } else {
                tryNextCompression();
              }
            }, attempt.format, attempt.quality);
          };
          
          tryNextCompression();
          
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => reject(new Error('Failed to load image for compression'));
      img.src = URL.createObjectURL(file);
    });
  };

  const processFiles = async (files) => {
    const capabilities = getModelCapabilities(selectedModel);
    const acceptedFiles = getAcceptedFiles(selectedModel);
    const supportsVision = capabilities.vision;
    
    const filePromises = [];
    
    for (const file of files) {
      const filePromise = new Promise(async (resolve, reject) => {
        // Handle PDF files first with text extraction
        if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
          try {
            const extractedText = await extractPDFText(file);
            console.log('PDF text extracted:', file.name, 'Text length:', extractedText.length);
            
            // Create a data URL for PDF preview
            const arrayBuffer = await file.arrayBuffer();
            const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
            const pdfDataUrl = URL.createObjectURL(blob);
            
            const attachment = {
              type: 'file',
              name: file.name,
              size: file.size,
              data: extractedText, // Send extracted text to backend
              mimeType: 'text/plain',
              isTextFile: true,
              isPdfFile: true,
              isDocFile: false,
              modelSupported: true,
              warningMessage: null,
              previewUrl: pdfDataUrl // Keep original PDF for preview
            };
            console.log('Adding PDF text attachment:', attachment);
            resolve(attachment);
            return;
          } catch (error) {
            console.error('Failed to extract PDF text:', error);
            reject(error);
            return;
          }
        }
        
        if (file.type.startsWith('image/')) {
          if (!supportsVision || !acceptedFiles.includes('image')) {
            const proceed = window.confirm('This model does not support images. Upload anyway?');
            if (!proceed) {
              resolve(null);
              return;
            }
          }
          
          try {
            // Always compress images larger than 100KB
            const shouldCompress = file.size > 100000; // 100KB threshold
            
            if (shouldCompress) {
              console.log(`Image ${file.name} is ${(file.size/1024).toFixed(1)}KB, compressing...`);
              
              // Use aggressive compression for very large files
              let quality = 0.7;
              let maxWidth = 1920;
              let maxHeight = 1080;
              
              if (file.size > 2000000) { // 2MB+
                quality = 0.6;
                maxWidth = 1280;
                maxHeight = 720;
              } else if (file.size > 5000000) { // 5MB+
                quality = 0.5;
                maxWidth = 1024;
                maxHeight = 768;
              }
              
              const compressed = await compressImage(file, quality, maxWidth, maxHeight);
              
              // Analyze compression effectiveness
              const compressionRatio = compressed.compressionRatio;
              const isEffectiveCompression = compressionRatio > 15; // At least 15% reduction
              const wasAlreadyOptimized = compressionRatio < 5; // Less than 5% means already well compressed
              
              let compressionMessage = '';
              if (wasAlreadyOptimized) {
                compressionMessage = `File was already well optimized (${compressionRatio.toFixed(1)}% reduction)`;
              } else if (!isEffectiveCompression) {
                compressionMessage = `Limited compression possible (${compressionRatio.toFixed(1)}% reduction)`;
              } else {
                compressionMessage = `Optimized by ${compressionRatio.toFixed(1)}% using ${compressed.strategy}`;
              }
              
              const attachment = {
                type: 'image',
                name: file.name,
                data: compressed.data,
                size: compressed.size,
                mimeType: compressed.outputFormat || file.type,
                originalSize: file.size,
                compressed: true,
                compressionRatio: compressionRatio.toFixed(1),
                isEffectiveCompression: isEffectiveCompression,
                wasAlreadyOptimized: wasAlreadyOptimized,
                compressionMessage: compressionMessage,
                compressionStrategy: compressed.strategy,
                formatChanged: compressed.originalFormat !== compressed.outputFormat,
                dimensionsChanged: compressed.dimensionsChanged
              };
              
              console.log(`Image compression complete: ${attachment.name}`);
              console.log(`- Original: ${(file.size/1024).toFixed(1)}KB (${file.type})`);
              console.log(`- Final: ${(attachment.size/1024).toFixed(1)}KB (${attachment.mimeType})`);
              console.log(`- Strategy: ${compressed.strategy}`);
              console.log(`- Result: ${attachment.compressionMessage}`);
              
              resolve(attachment);
            } else {
              // Small image, process normally but still convert to base64
              const reader = new FileReader();
              reader.onload = (e) => {
                console.log('Small image processed without compression:', file.name, `${(file.size/1024).toFixed(1)}KB`);
                const attachment = {
                  type: 'image',
                  name: file.name,
                  data: e.target.result,
                  size: file.size,
                  mimeType: file.type,
                  compressed: false
                };
                resolve(attachment);
              };
              reader.onerror = (e) => {
                console.error('Error reading small image file:', file.name, e);
                reject(new Error(`Failed to read image file: ${file.name}`));
              };
              reader.readAsDataURL(file);
            }
          } catch (error) {
            console.error('Error processing image:', file.name, error);
            // Fallback to normal processing if compression fails
            const reader = new FileReader();
            reader.onload = (e) => {
              console.log('Fallback processing for image:', file.name);
              const attachment = {
                type: 'image',
                name: file.name,
                data: e.target.result,
                size: file.size,
                mimeType: file.type,
                compressed: false,
                compressionFailed: true
              };
              resolve(attachment);
            };
            reader.onerror = (e) => reject(new Error(`Failed to process image: ${file.name}`));
            reader.readAsDataURL(file);
          }
        } else {
          // Expanded text file detection
          const textExtensions = [
            '.txt', '.md', '.json', '.xml', '.csv', '.js', '.jsx', '.ts', '.tsx',
            '.html', '.htm', '.css', '.scss', '.sass', '.less', '.vue', '.svelte',
            '.php', '.py', '.rb', '.java', '.c', '.cpp', '.h', '.hpp', '.cs',
            '.go', '.rs', '.kt', '.swift', '.sql', '.sh', '.bash', '.zsh',
            '.yml', '.yaml', '.toml', '.ini', '.cfg', '.conf', '.env',
            '.njk', '.ejs', '.hbs', '.mustache', '.twig', '.liquid',
            '.r', '.rmd', '.matlab', '.m', '.pl', '.scala', '.clj', '.lua',
            '.dart', '.elm', '.ex', '.exs', '.fs', '.fsx', '.vb', '.pas'
          ];
          
          const fileName = file.name.toLowerCase();
          const isTextFile = file.type.startsWith('text/') || 
                            textExtensions.some(ext => fileName.endsWith(ext));
          
          const isPdfFile = file.type === 'application/pdf' || fileName.endsWith('.pdf');
          const isDocFile = fileName.endsWith('.doc') || fileName.endsWith('.docx');
          
          // Check if current model supports this file type
          const acceptedFiles = getAcceptedFiles(selectedModel);
          const modelSupportsFile = acceptedFiles.includes('pdf') || 
                                  acceptedFiles.includes('docx') || 
                                  acceptedFiles.includes('txt') || 
                                  acceptedFiles.includes('image');
          
          const reader = new FileReader();
          reader.onload = (e) => {
            console.log('File processed:', file.name, 'Data type:', typeof e.target.result, 'Is text file:', isTextFile);
            let fileData = e.target.result;
            
            // Add model compatibility info to attachment
            const modelSupportsPdf = acceptedFiles.includes('pdf');
            const modelSupportsDocx = acceptedFiles.includes('docx');
            
            const attachment = {
              type: 'file',
              name: file.name,
              size: file.size,
              data: fileData,
              mimeType: file.type || (isTextFile ? 'text/plain' : isPdfFile ? 'application/pdf' : isDocFile ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' : 'application/octet-stream'),
              isTextFile: isTextFile,
              isPdfFile: isPdfFile,
              isDocFile: isDocFile,
              extractText: isPdfFile || isDocFile, // Flag for server-side text extraction
              modelSupported: isTextFile || (isPdfFile && modelSupportsPdf) || (isDocFile && modelSupportsDocx),
              warningMessage: isPdfFile && !modelSupportsPdf ? 
                `⚠️ This model may not be able to read PDF files directly` : 
                (!isTextFile && !modelSupportsPdf && !modelSupportsDocx ? 
                  `⚠️ This model may not be able to read this file type` : null)
            };
            console.log('Adding file attachment:', attachment);
            resolve(attachment);
          };
          reader.onerror = (e) => {
            console.error('Error reading file:', file.name, e);
            reject(new Error(`Failed to read file: ${file.name}`));
          };
          
          // Use appropriate reading method based on file type
          if (isTextFile) {
            reader.readAsText(file, 'UTF-8');
          } else {
            // For PDFs, Word docs, and other binary files, use readAsDataURL for base64 encoding
            reader.readAsDataURL(file);
          }
        }
      });
      
      filePromises.push(filePromise);
    }
    
    try {
      const processedAttachments = await Promise.all(filePromises);
      const validAttachments = processedAttachments.filter(att => att !== null);
      setAttachments(prev => [...prev, ...validAttachments]);
    } catch (error) {
      console.error('Error processing files:', error);
      alert('Error processing some files');
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    console.log('Files selected:', files.length, files.map(f => f.name));
    processFiles(files);
    // Clear the input so the same file can be selected again
    event.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData.items);
    items.forEach(item => {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          processFiles([file]);
        }
      }
    });
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  return {
    attachments,
    setAttachments,
    fileInputRef,
    handleFileUpload,
    handleDrop,
    handleDragOver,
    handlePaste,
    removeAttachment,
    supportsVision: getModelCapabilities(selectedModel).vision,
    acceptedFiles: getAcceptedFiles(selectedModel)
  };
};